import { limit, orderBy, query } from 'firebase/firestore';
import { useCallback, useMemo } from 'react';
import { useUsers } from '@/lib/directory/use-users';
import { useCollection } from '@/lib/firestore/hooks';
import { articlesCol, changesCol, cisCol, incidentsCol, problemsCol } from '@/lib/firestore/refs';
import type { Article, Change, ConfigurationItem, Incident, Problem } from '@shared/types';
import { rankResults, type SearchHit } from './rank';

/** Client-side search over recent records, knowledge, CIs and people. */
export function useGlobalSearch() {
  const inc = useCollection<Incident>(useMemo(() => query(incidentsCol(), orderBy('updatedAt', 'desc'), limit(500)), []), []);
  const prb = useCollection<Problem>(useMemo(() => query(problemsCol(), orderBy('updatedAt', 'desc'), limit(200)), []), []);
  const chg = useCollection<Change>(useMemo(() => query(changesCol(), orderBy('updatedAt', 'desc'), limit(200)), []), []);
  const kb = useCollection<Article>(useMemo(() => query(articlesCol(), orderBy('updatedAt', 'desc'), limit(300)), []), []);
  const cis = useCollection<ConfigurationItem>(useMemo(() => query(cisCol(), orderBy('name'), limit(500)), []), []);
  const { users } = useUsers();
  const hits = useMemo<SearchHit[]>(
    () => [
      ...inc.data.map((x) => ({ type: 'incident' as const, id: x.id, title: x.short, desc: x.desc, route: `/incidents/${x.id}` })),
      ...prb.data.map((x) => ({ type: 'problem' as const, id: x.id, title: x.short, desc: x.desc, route: `/problems/${x.id}` })),
      ...chg.data.map((x) => ({ type: 'change' as const, id: x.id, title: x.short, desc: x.description, route: `/changes/${x.id}` })),
      ...kb.data.map((x) => ({ type: 'article' as const, id: x.id, title: x.title, desc: x.summary, route: `/knowledge/${x.id}` })),
      ...cis.data.map((x) => ({ type: 'ci' as const, id: x.id, title: x.name, desc: `${x.cls} ${x.ip}`, route: `/cmdb/${x.id}` })),
      ...users.map((x) => ({ type: 'user' as const, id: x.uid, title: x.name, desc: `${x.dept} ${x.email}`, route: '/catalog' })),
    ],
    [inc.data, prb.data, chg.data, kb.data, cis.data, users],
  );
  return useCallback((q: string) => rankResults(hits, q), [hits]);
}
